import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/AuthContext';
import { listMembers, type HouseholdMember } from '@/lib/api';

export type OwnerFilterValue = 'all' | 'mine' | 'shared' | `user:${string}`;

/** Nomes e ids dos membros do household, pra rotular donos de contas/cartões. */
export function useOwnership() {
  const { me } = useAuth();
  const householdId = me?.household?.id ?? '';
  const membersQ = useQuery({
    queryKey: ['members', householdId],
    queryFn: () => listMembers(householdId),
    enabled: householdId !== '',
    staleTime: 5 * 60_000,
  });
  const myId = me?.user?.id ?? '';
  const members: HouseholdMember[] = membersQ.data ?? [];
  const firstName = (userId: string) =>
    members.find((m) => m.user_id === userId)?.user.full_name.split(' ')[0] ?? '?';
  const others = members.filter((m) => m.user_id !== myId);
  return { myId, firstName, others };
}

export function matchesOwner(
  value: OwnerFilterValue,
  ownerId: string,
  isShared: boolean,
  myId: string,
): boolean {
  if (value === 'all') return true;
  if (value === 'mine') return ownerId === myId;
  if (value === 'shared') return isShared;
  return ownerId === value.slice('user:'.length);
}

/** Chip com o dono: nada se é meu, 👤 Nome se de outro membro, 👥 se compartilhada. */
export function OwnerBadge({ ownerId, isShared }: { ownerId: string; isShared: boolean }) {
  const { myId, firstName } = useOwnership();
  if (isShared) {
    return (
      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-700">
        👥 compartilhada
      </span>
    );
  }
  if (!myId || ownerId === myId) return null;
  return (
    <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">
      👤 {firstName(ownerId)}
    </span>
  );
}

/** Linha de chips pra filtrar por dono: todas | minhas | <cada outro membro> | compartilhadas. */
export function OwnerFilter({
  value,
  onChange,
}: {
  value: OwnerFilterValue;
  onChange: (v: OwnerFilterValue) => void;
}) {
  const { others } = useOwnership();
  const options: { value: OwnerFilterValue; label: string }[] = [
    { value: 'all', label: 'todas' },
    { value: 'mine', label: 'minhas' },
    ...others.map((m) => ({
      value: `user:${m.user_id}` as OwnerFilterValue,
      label: `de ${m.user.full_name.split(' ')[0]}`,
    })),
    { value: 'shared', label: 'compartilhadas' },
  ];
  if (others.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`tap rounded-full border px-3 py-1.5 text-sm ${
            value === o.value
              ? 'border-slate-900 bg-slate-900 text-white'
              : 'border-slate-300 text-slate-600'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
